// @ts-nocheck
/*
 * Port of views/dashboard/numbers/create.js (453 lines).
 * BYTE-IDENTICAL — Individual + File submission branches, upload-result
 * modal with 4 sections (summary/created/failed/free-cost-errors),
 * stripCallingCodeFromDigits sanitizes DID against country calling code
 * before POST. Chrome swap: v1 <Card> → shadcn.
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card as ShadcnCard } from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { postApiMediaCall } from '@/service/postApiCall';
import { API_COUNTRY_CODES_LIST, API_NUMBERS_UPLOAD, API_NUMBERS_CREATE } from '@/service/url';
import { useNavigate } from 'react-router-dom';
import { NUMBERS_FORMAT_INDIVIDUAL, NUMBERS_FORMAT_FILE, ROUTES, NUMBER_COST_MIN, NUMBER_COST_MAX } from '@/utilities/constant';
import { stripCallingCodeFromDigits, resolveCallingCodeByCountryIso } from '@/utilities/numberCountryCode';
import RenderNumberForm from '@/features/numbers/NumberForm';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';

const normalizeUploadResult = (result) => {
  if (!result || typeof result !== 'object') return null;
  const failedNumbers = Array.isArray(result.failedNumbers) ? result.failedNumbers : [];
  const successfulNumbers = Array.isArray(result.successfulNumbers)
    ? result.successfulNumbers
    : Array.isArray(result.validFreeDIDs)
      ? result.validFreeDIDs
      : [];
  return {
    totalProcessed: Number(result.totalProcessed || 0),
    successCount: Number(result.successCount ?? successfulNumbers.length ?? 0),
    failedCount: Number(result.failedCount ?? failedNumbers.length ?? 0),
    successfulNumbers,
    failedNumbers,
    invalidFreeDIDs: Array.isArray(result.invalidFreeDIDs) ? result.invalidFreeDIDs : [],
    validFreeDIDs: Array.isArray(result.validFreeDIDs) ? result.validFreeDIDs : [],
  };
};

const formatFailedNumber = (failure) => {
  if (!failure) return '';
  if (typeof failure === 'string') return failure;
  const row = failure.row ?? failure.rowNumber;
  const did = failure.did ?? 'N/A';
  const column = failure.column || 'Validation';
  const reason = failure.reason || 'Upload failed';
  const rowLabel = row && row !== 'N/A' ? `Row ${row}` : 'Row unknown';
  return `${rowLabel} | DID ${did} | ${column}: ${reason}`;
};

export default function NumbersCreate() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [formatType, setFormatType] = useState('');
  const [countryCodeId, setCountryCodeId] = useState('');
  const [numberType, setNumberType] = useState('');
  const [category, setCategory] = useState('');
  const [currency, setCurrency] = useState('');
  const [did, setDid] = useState('');
  const [cost, setCost] = useState('');
  const [operator, setOperator] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [uploadFile, setUploadFile] = useState(undefined);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [buttonFileDisable, setButtonFileDisable] = useState(false);
  const [actualNumber, setActualNumber] = useState('');
  const [trunks, setTrunks] = useState('');
  const [showResultModal, setShowResultModal] = useState(false);
  const [numberUploadResult, setNumberUploadResult] = useState(false);
  const [numberRoutingType, setNumberRoutingType] = useState('');
  const [countryCodesList, setCountryCodesList] = useState([]);
  const [linkedDidId, setLinkedDidId] = useState('');

  useEffect(() => {
    if (category === 'Free') setCost('0');
  }, [category]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    getApiCall(API_COUNTRY_CODES_LIST, {})
      .then((resp) => setCountryCodesList(Array.isArray(resp) ? resp : []))
      .catch(() => setCountryCodesList([]));
  }, []);

  function validateForm() {
    let valid = true;
    if (!formatType) {
      valid = false;
      toast.error('Format Type required');
    } else if (formatType === NUMBERS_FORMAT_FILE) {
      if (!isFileUpload && !uploadFile) {
        toast.error('Numbers File is Required');
        valid = false;
      }
      if (!numberType) {
        valid = false;
        toast.error('Number Type is required');
      } else if (numberType === 'DID' && !category) {
        valid = false;
        toast.error('Category is required for DID');
      } else if (!currency) {
        valid = false;
        toast.error('Currency is required');
      }
    } else if (!numberType) {
      valid = false;
      toast.error('Number Type is required');
    } else if (numberType === 'DID' && !category) {
      valid = false;
      toast.error('Category is required for DID');
    } else if (!currency) {
      valid = false;
      toast.error('Currency is required');
    } else if (numberType === 'DID' && !trunks) {
      valid = false;
      toast.error('Trunk is required');
    } else if (formatType === NUMBERS_FORMAT_INDIVIDUAL && !numberRoutingType) {
      valid = false;
      toast.error('Number Routing Type required');
    } else if (formatType === NUMBERS_FORMAT_INDIVIDUAL) {
      if (!countryCodeId) {
        valid = false;
        toast.error('Country is required');
      } else if (!did) {
        valid = false;
        toast.error('DID is required');
      } else if (!cost) {
        valid = false;
        toast.error('Cost is required');
      } else if (
        !Number.isFinite(Number(cost)) ||
        Number(cost) < NUMBER_COST_MIN ||
        Number(cost) > NUMBER_COST_MAX
      ) {
        valid = false;
        toast.error(`Cost must be between ${NUMBER_COST_MIN} and ${NUMBER_COST_MAX}`);
      } else if (!operator) {
        valid = false;
        toast.error('Operator is required');
      } else if (numberType === 'DID' && !stateCode) {
        valid = false;
        toast.error('State Code is required for DID');
      } else if ((numberType === 'VMN' || numberType === 'TOLL FREE') && !linkedDidId) {
        valid = false;
        toast.error('DID Number is required for VMN/TOLL FREE');
      }
    }
    return valid;
  }

  async function submitFileForm() {
    const formData = new FormData();
    formData.append('pool', 'pool');
    formData.append('phone_numbers', uploadFile);
    formData.append('numberType', numberType);
    formData.append('category', category);
    formData.append('currency', currency);
    formData.append('phone_number', NUMBERS_FORMAT_FILE);

    postApiMediaCall(API_NUMBERS_UPLOAD, formData).then(onNumbersUploadSuccess).catch(onNumbersUploadError);
  }

  function onNumbersUploadError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to upload numbers');
    }
  }

  function onNumberCreateSuccess() {
    toast.success('Numbers created successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.NUMBERS.LIST}`);
    }, 1500);
  }

  function onNumbersUploadSuccess(resp) {
    const normalized = normalizeUploadResult(resp);
    setNumberUploadResult(normalized);
    setButtonDisable(false);
    setShowResultModal(true);

    if (!normalized) {
      toast.error('Upload completed but result details were unavailable.');
      return;
    }

    if (normalized.successCount > 0 && normalized.failedCount === 0) {
      toast.success(`${normalized.successCount} number(s) uploaded successfully.`);
    } else if (normalized.successCount > 0) {
      toast.warning(`Uploaded ${normalized.successCount} number(s); ${normalized.failedCount} row(s) failed.`);
    } else {
      toast.error(`No numbers were created. ${normalized.failedCount} row(s) failed validation.`);
    }
  }

  function onNumberCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.error('Number creation error:', err);
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to create number');
    }
  }

  async function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);
    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    if (formatType === NUMBERS_FORMAT_FILE) {
      submitFileForm();
    } else if (formatType === NUMBERS_FORMAT_INDIVIDUAL) {
      const callingCode = resolveCallingCodeByCountryIso(countryCodesList, countryCodeId);
      const nationalDid = stripCallingCodeFromDigits(did, callingCode);
      const didDigits = String(did || '').replace(/\D/g, '');
      const didForApi = (nationalDid || didDigits).trim();
      if (!didForApi) {
        setButtonDisable(false);
        toast.error('DID is required');
        return;
      }

      let params: any = {
        countryCode: countryCodeId,
        numberType,
        currency,
        DID: didForApi,
        cost: category === 'Free' ? '0' : cost,
        operator,
        number_routing_type: numberRoutingType,
      };
      if (numberType === 'DID') {
        params.category = category;
        params.stateCode = stateCode;
        params.trunk_id = trunks;
      }
      if (numberType === 'VMN' || numberType === 'TOLL FREE') {
        params.linkedDidId = linkedDidId;
      }

      postApiCall(API_NUMBERS_CREATE, params).then(onNumberCreateSuccess).catch(onNumberCreateError);
    }
  }

  return (
    <ScrollPage>
      <PageHeader
        title="New Numbers"
        description="Upload / register DID / VMN / Toll-free numbers and assign a voice plan."
      />
      <div className="row">
        <div className="col-12">
          <ShadcnCard>
            <div className="p-4">
              <form onSubmit={async (e) => { submitForm(e); }}>
                <RenderNumberForm
                  countryCodeId={countryCodeId}
                  setCountryCodeId={setCountryCodeId}
                  formatType={formatType}
                  setFormatType={setFormatType}
                  uploadFile={uploadFile}
                  setUploadFile={setUploadFile}
                  isFileUpload={isFileUpload}
                  setIsFileUpload={setIsFileUpload}
                  numberType={numberType}
                  setNumberType={setNumberType}
                  category={category}
                  setCategory={setCategory}
                  currency={currency}
                  setCurrency={setCurrency}
                  actualNumber={actualNumber}
                  setActualNumber={setActualNumber}
                  cost={cost}
                  setCost={setCost}
                  operator={operator}
                  setOperator={setOperator}
                  stateCode={stateCode}
                  setStateCode={setStateCode}
                  did={did}
                  setDid={setDid}
                  buttonFileDisable={buttonFileDisable}
                  setButtonFileDisable={setButtonFileDisable}
                  buttonDisable={buttonDisable}
                  setButtonDisable={setButtonDisable}
                  toastProps={toastProps}
                  setToastProps={setToastProps}
                  trunks={trunks}
                  setTrunks={setTrunks}
                  numberRoutingType={numberRoutingType}
                  setNumberRoutingType={setNumberRoutingType}
                  linkedDidId={linkedDidId}
                  setLinkedDidId={setLinkedDidId}
                />

                <div className="mt-4 flex justify-end">
                  <Button type={'submit'} disabled={buttonDisable}>
                    Add Numbers
                    {buttonDisable && (
                      <span
                        className="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </ShadcnCard>
        </div>
      </div>

      <Dialog open={showResultModal} onOpenChange={(next) => !next && setShowResultModal(false)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Number Upload Result</DialogTitle>
          </DialogHeader>
          <div style={{ maxWidth: '800px' }}>
            {numberUploadResult && (
              <>
                <div className="mb-4">
                  <h5 className="mb-2">Upload Summary</h5>
                  <p className="mb-1">Processed: {numberUploadResult.totalProcessed}</p>
                  <p className="mb-1 text-success">Created: {numberUploadResult.successCount}</p>
                  <p className="mb-0 text-danger">Failed: {numberUploadResult.failedCount}</p>
                </div>

                {numberUploadResult.successfulNumbers?.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-success mb-2">Numbers Created</h5>
                    <textarea
                      readOnly
                      rows={4}
                      className="form-control"
                      value={numberUploadResult.successfulNumbers.join('\n')}
                    />
                  </div>
                )}

                {numberUploadResult.failedNumbers?.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-danger mb-2">Failed Rows</h5>
                    <textarea
                      readOnly
                      rows={6}
                      className="form-control"
                      value={numberUploadResult.failedNumbers.map((failure) => formatFailedNumber(failure)).join('\n')}
                    />
                  </div>
                )}

                {numberUploadResult.invalidFreeDIDs?.length > 0 && (
                  <div>
                    <h5 className="text-danger mb-2">Free Category Cost Errors</h5>
                    <textarea
                      readOnly
                      rows={4}
                      className="form-control"
                      value={numberUploadResult.invalidFreeDIDs
                        .map((fail) => `${fail} - Cost is not zero for Free category`)
                        .join('\n')}
                    />
                  </div>
                )}

                {numberUploadResult.successCount === 0 && numberUploadResult.failedCount === 0 && (
                  <p className="mb-0 text-muted">No rows were processed from the uploaded file.</p>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollPage>
  );
}
